/** Local stand-in for the retired readiness-gate parser (removed) — kept only
 *  so `hardBlockReason` (which reuses its copy) doesn't depend on any removed
 *  type. */
type BlockKind =
  | { kind: "screening_blocked"; ats: string }
  | { kind: "name_required"; ats: string }
  | {
      kind: "needs_fields";
      ats: string;
      needsEmail: boolean;
      needsCountry: boolean;
      emailUnrevealed: boolean;
    }
  | { kind: "none" };

function gateExplanation(gate: BlockKind, ats: string): string {
  if (gate.kind === "screening_blocked") {
    return `This ${ats} job requires application questions Arbi can't auto-answer — add this candidate directly in ${ats}.`;
  }
  if (gate.kind === "name_required") {
    return `Add the candidate's name in Arbi before pushing to ${ats}.`;
  }
  if (gate.kind === "needs_fields") {
    const parts: string[] = [];
    if (gate.needsEmail) parts.push("an email address");
    if (gate.needsCountry) parts.push("a country");
    const fields =
      parts.length === 2 ? `${parts[0]} and ${parts[1]}` : parts[0] ?? "";
    return `Add ${fields} to sync to ${ats}.`;
  }
  return "";
}

/** True when the hard-block is fillable in Arbi (side sheet), not permanent add-in-ATS. */
export function isFillableApplicationBlock(
  blockCodes: string[] | undefined | null,
): boolean {
  return (blockCodes ?? []).includes("unanswered_application_questions");
}

export function showFillAnswersCta(opts: {
  softHint?: boolean;
  blockCodes?: string[] | null;
}): boolean {
  return (
    opts.softHint === true || isFillableApplicationBlock(opts.blockCodes)
  );
}

/** Human-readable reason for a hard_blocked candidate, reusing the readiness
 *  gate's copy so the sync modal reads exactly like the retired dialog. */
export function hardBlockReason(
  blockCodes: string[],
  missingFields: string[],
  ats: string,
): string {
  // Codes are PromotionBlockCode values (backend promotion.py).
  // Fillable unanswered questions take precedence over permanent screening
  // blocks so the sync modal can deep-link to the answers sheet (#20).
  if (isFillableApplicationBlock(blockCodes)) {
    return "Needs application answers — fill in Arbi";
  }
  if (blockCodes.includes("required_screening_questions")) {
    return gateExplanation({ kind: "screening_blocked", ats }, ats);
  }
  if (blockCodes.includes("missing_name")) {
    return gateExplanation({ kind: "name_required", ats }, ats);
  }
  const needsEmail = missingFields.includes("email");
  const needsCountry = missingFields.includes("country");
  if (needsEmail || needsCountry) {
    return gateExplanation(
      { kind: "needs_fields", ats, needsEmail, needsCountry, emailUnrevealed: false },
      ats,
    );
  }
  return `This profile can't be synced to ${ats}; fix it in the profile and try again.`;
}
